import * as React from 'react';
import { AppProvider, Router, type Session, type Navigation } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Divider, IconButton, ListItemIcon, Menu, MenuItem, MenuList, Stack, Tooltip, Typography, useMediaQuery, type SxProps } from '@mui/material';
import type { AvatarProps } from '@mui/material/Avatar';
import { styled, alpha, useColorScheme, useTheme, type Theme } from '@mui/material/styles';
import { ProtectedRoute } from '../services/common/protected-route';
import LanguageSwitchComponent from './language-switch';
import getNavigation from './navigation';
import { useAuth } from '../services/common/auth-context';
import { useTranslation } from 'react-i18next';
import getTheme from './theme';
import i18n from './i18n';
import { LanguageCode } from '../helper-types/general/consts/language-code';
import { usePWAUpdate } from '../hooks/usePWAUpdate';
import { useEnrollmentRequestsPendingCount } from '../hooks/useEnrollmentRequestsPendingCount';
import { Account } from '@toolpad/core';
import { AccountPreview, AccountPopoverHeader, AccountPopoverFooter, SignOutButton } from '@toolpad/core/Account';
import { DarkMode, LightMode, Login, PersonRounded, School, Settings as SettingsIcon } from '@mui/icons-material';
import { SystemRole } from '../helper-types/general/enums/system-role';
import { SystemClaim } from '../helper-types/general/enums/system-claim';
import { Container } from '@mui/system';
import AIChatPanel from '../components/ai/ai-chat-panel';
import AIChatTriggerButton from '../components/ai/ai-chat-trigger-button';
import NotificationBell from '../components/notification/notification-bell';
import BottomNavBar, { BOTTOM_NAV_HEIGHT } from '../components/navigation/bottom-nav-bar';
import { getShellGradient, getShellGlow } from './shell-accent';
import AppVersionFooter from '../components/common/app-version-footer';

// Lazy-loaded components
const DashboardPage = React.lazy(() => import('../pages/dashboard/dashboard-page'));
const SubjectTreePage = React.lazy(() => import('../pages/subject/subject-tree-page'));
const CoursePage = React.lazy(() => import('../pages/course/course-page'));
const CourseItemPage = React.lazy(() => import('../pages/course/course-item-page'));
const QuestionsBankPage = React.lazy(() => import('../pages/questions-bank/questions-bank-page'));
const QuestionPage = React.lazy(() => import('../pages/question/question-page'));
const ExamPage = React.lazy(() => import('../pages/exam/exam-page'));
const PricingPlanPage = React.lazy(() => import('../pages/pricing-plan/pricing-plan-page'));
const LicensePage = React.lazy(() => import('../pages/license/license-page'));
const EnrollmentRequestsPage = React.lazy(() => import('../pages/enrollment-request/enrollment-requests-page'));
const RolePage = React.lazy(() => import('../pages/role/role-page'));
const UserPage = React.lazy(() => import('../pages/user/user-page'));
const UserProfilePage = React.lazy(() => import('../pages/user/user-profile-page'));
const TenantBrandingPage = React.lazy(() => import('../pages/tenant-settings/tenant-branding-page'));
const TenantIntegrationsPage = React.lazy(() => import('../pages/tenant-settings/tenant-integrations-page'));
const TenantPaymentChannelsPage = React.lazy(() => import('../pages/tenant-settings/tenant-payment-channels-page'));
const StudentManagementPage = React.lazy(() => import('../pages/student/student-management-page'));
const StudentResultsPage = React.lazy(() => import('../pages/student-results/student-results-page'));
const LiveRoomsPage = React.lazy(() => import('../pages/live-rooms/live-rooms-page'));
const AttendancePage = React.lazy(() => import('../pages/attendance/attendance-page'));
const FinancialReportsPage = React.lazy(() => import('../pages/financial-reports/financial-reports-page'));
const LiveRoomPage = React.lazy(() => import('../pages/live-rooms/live-room-page'));
const MyLiveRoomsPage = React.lazy(() => import('../pages/student/my-live-rooms-page'));
const LoginPage = React.lazy(() => import('../pages/auth/login/login-page'));
const RegisterPage = React.lazy(() => import('../pages/auth/register/register-page'));
const MyCoursesPage = React.lazy(() => import('../pages/student/my-courses-page'));
const MyCourseDetailPage = React.lazy(() => import('../pages/student/my-course-detail-page'));
const MyExamsPage = React.lazy(() => import('../pages/student/my-exams-page'));
const StudentProfilePage = React.lazy(() => import('../pages/student/student-profile-page'));
const TakeExamPage = React.lazy(() => import('../pages/student/take-exam-page'));
const UnauthorizedPage = React.lazy(() => import('../pages/common/unauthorized-page'));
const NotFoundPage = React.lazy(() => import('../pages/common/not-found-page'));
const MorePage = React.lazy(() => import('../pages/common/more-page'));

// Styled branding logo with subtle hover effect
const BrandingLogo = styled('img')(({ theme }) => ({
    transition: 'transform 0.3s ease, filter 0.3s ease',
    '&:hover': {
        transform: 'scale(1.08)',
        filter: `drop-shadow(0 2px 8px ${getShellGlow(theme, 0.35)})`,
    },
}));

// Page content wrapper with fade-in transition
const PageContent = styled(Container)(({ theme }) => ({
    padding: theme.spacing(2, 2.5),
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    animation: 'pageSlideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    '@keyframes pageSlideIn': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    // Clears the fixed BottomNavBar on mobile so it never covers page content.
    [theme.breakpoints.down('sm')]: {
        paddingBottom: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom) + ${theme.spacing(2)})`,
    },
}));

// Spring-like easing used for the shell's micro-interactions (nav hover/selected,
// page transitions) — matches the curve already used by AIChatTriggerButton.
const SHELL_SPRING_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// DashboardLayout sx overrides for IMEAG look
const getDashboardLayoutSx = (theme: Theme): SxProps => ({
    // AppBar — layered glass: deeper blur, a soft brand-tinted glow, and a
    // gradient hairline along the bottom edge so the brand accent reads clearly
    // even at a glance.
    '& .MuiAppBar-root': {
        // No `position` override here — Toolpad already renders this AppBar with
        // `position="absolute"` (needed for its own overlay/flex layout math); forcing
        // `relative` pulled it back into normal flex flow and broke the whole page
        // layout. `position: absolute` is already a valid containing block for the
        // `::after` pseudo below, so no override is needed for that either.
        backdropFilter: 'blur(22px) saturate(1.4)',
        backgroundColor: alpha('#ffffff', 0.72),
        borderBottom: 'none',
        boxShadow: `0 1px 2px ${alpha('#000', 0.05)}, 0 12px 32px -10px ${getShellGlow(theme, 0.35)}`,
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        '&::after': {
            content: '""',
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            bottom: 0,
            height: 2,
            backgroundImage: getShellGradient(theme, '90deg'),
            opacity: 0.7,
        },
        ...theme.applyStyles('dark', {
            backgroundColor: alpha('#1E1E2F', 0.7),
            boxShadow: `0 1px 2px ${alpha('#000', 0.4)}, 0 12px 36px -10px ${getShellGlow(theme, 0.45)}`,
        }),
    },

    // The hamburger menu toggle (Toolpad's own, no exposed prop to disable it) is
    // redundant once BottomNavBar covers the same breakpoint — matches BottomNavBar's
    // own `down('sm')` visibility exactly, so tablet sizes (where BottomNavBar doesn't
    // render) keep it unchanged. Targeted by its aria-label since Toolpad doesn't give
    // it a dedicated class.
    '& [aria-label$="navigation menu"]': {
        [theme.breakpoints.down('sm')]: {
            display: 'none !important',
        },
    },

    // Keep the toolbar (title + actions) on a single line on mobile instead
    // of wrapping the language/theme/account controls onto a second row.
    '& .MuiToolbar-root > .MuiStack-root': {
        [theme.breakpoints.down('sm')]: {
            flexWrap: 'nowrap',
        },
    },

    // Trim the top bar a little on sm+ (MUI's Toolbar default is 64px there, 56px on mobile
    // already) — mobile is left as-is.
    '& .MuiToolbar-root': {
        [theme.breakpoints.up('sm')]: {
            minHeight: 56,
        },
    },

    // Sidebar's own nav container (Toolpad renders it as a plain `<nav>`, no class to target):
    // Toolpad lays it out as a column flexbox with `justifyContent: space-between` between the
    // item list and the version-footer slot, which pins the footer to the very bottom of the
    // drawer — leaving one big blank gap above it whenever the nav items don't fill the sidebar's
    // full height. Overriding to `flex-start` makes the footer follow directly after the last
    // nav item instead, at the cost of it no longer always sitting flush at the bottom edge.
    '& .MuiDrawer-root nav': {
        justifyContent: 'flex-start',
    },

    // Sidebar drawer — subtle gradient wash, layered depth, and a matching
    // gradient hairline on the outer edge (mirrors the app bar's bottom line).
    '& .MuiDrawer-root .MuiDrawer-paper': {
        // Same reasoning as the AppBar above — Toolpad already sets this to
        // `position: absolute` itself; don't override it.
        backgroundImage: `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f5f7fa', 0.95)} 100%)`,
        borderRight: 'none',
        borderLeft: 'none',
        boxShadow: `4px 0 32px -10px ${getShellGlow(theme, 0.28)}`,
        transition: 'background 0.3s ease, box-shadow 0.3s ease',
        '&::after': {
            content: '""',
            position: 'absolute',
            insetInlineEnd: 0,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundImage: getShellGradient(theme, '180deg'),
            opacity: 0.6,
        },
        ...theme.applyStyles('dark', {
            backgroundImage: `linear-gradient(180deg, ${alpha('#1E1E2F', 0.97)} 0%, ${alpha('#121212', 0.99)} 100%)`,
            boxShadow: `4px 0 36px -10px ${getShellGlow(theme, 0.38)}`,
        }),
    },

    // Section headers (Toolpad's `kind: 'header'` items) — MUI's ListSubheader defaults to an
    // opaque `background.paper` fill spanning the drawer's full width, at its own z-index above
    // everything else in the list. That both (a) reads as a different color than the drawer's own
    // gradient wash above, and (b) paints straight over the gradient hairline on the drawer's
    // outer edge (the `::after` right above), cutting a visible gap in the line wherever a header
    // scrolls past it. Shrinking the header into an inset pill — background/border tinted with the
    // same brand gradient instead of a flat opaque fill — fixes both: it no longer touches that
    // outer edge at all, and its background reads as part of the same design language.
    '& .MuiDrawer-root .MuiListSubheader-root': {
        position: 'static',
        display: 'block',
        boxSizing: 'border-box',
        // Explicit calc instead of `width: 'auto'` — a plain "auto" width didn't reliably fill
        // the drawer's content box in this render context, leaving too little room for longer
        // titles and forcing Toolpad's own text-overflow:ellipsis (still active below) to kick
        // in and truncate them. This guarantees the full available width minus the two 12px
        // side margins, regardless of that ambiguity.
        width: `calc(100% - ${theme.spacing(3)})`,
        marginInlineStart: theme.spacing(1.5),
        marginInlineEnd: theme.spacing(1.5),
        marginBlockStart: theme.spacing(1.5),
        marginBlockEnd: theme.spacing(0.5),
        padding: theme.spacing(0.5, 1.5),
        height: 'auto',
        lineHeight: 1.6,
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        backgroundImage: 'none',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        color: theme.vars.palette.primary.main,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...theme.applyStyles('dark', {
            backgroundColor: alpha(theme.palette.primary.light, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
            color: theme.vars.palette.primary.light,
        }),
    },

    // Sidebar nav items — spring-eased hover, tinted wash on the selected item. Text color is
    // pinned to text.primary rather than any primary-color-derived value (contrastText, etc.):
    // the selected background here is only a faint alpha wash of primary.main, not a solid
    // fill, so text.primary is what's actually guaranteed to stay legible against it regardless
    // of what color a tenant picks — same principle as accessible-color.ts's fix for the brand
    // color itself, applied to this label specifically since it isn't fixed by that alone.
    '& .MuiDrawer-root .MuiListItemButton-root': {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '10px',
        margin: '2px 8px',
        transition: `background-color 0.25s ease, transform 0.35s ${SHELL_SPRING_EASING}`,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: 'translateX(2px)',
            ...theme.applyStyles('dark', {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
            }),
        },
        '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            fontWeight: 600,
            ...theme.applyStyles('dark', {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
            }),
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
                ...theme.applyStyles('dark', {
                    backgroundColor: alpha(theme.palette.primary.main, 0.25),
                }),
            },
            '& .MuiListItemIcon-root': {
                color: theme.vars.palette.primary.main,
            },
            '& .MuiListItemText-primary': {
                color: theme.vars.palette.text.primary,
            },
        },
    },

    // Nav icons — theme-aware color transition
    '& .MuiDrawer-root .MuiListItemIcon-root': {
        minWidth: 40,
        transition: 'color 0.25s ease',
    },
});

interface LayoutProps {
    setDirection: React.Dispatch<React.SetStateAction<'ltr' | 'rtl'>>;
}

// Fallback shown while a lazy-loaded page chunk is downloading. Without a
// Suspense boundary, navigating to a not-yet-downloaded page silently aborts
// the render (requiring repeated clicks until the chunk finishes loading).
const PageFallback = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            width: '100%',
        }}
    >
        <CircularProgress />
    </Box>
);

// Toolpad's ThemeSwitcher hardcodes its tooltip/aria-label in English
// (`${mode} mode`) with no prop to localize it, unlike Account's localeText. This is a
// drop-in replacement, translated via i18next — mirrors the pattern already used for the
// login page's own theme toggle. It reads/writes MUI's own useColorScheme() (the public,
// bundler-resolvable API) rather than Toolpad's internal PaletteModeContext — that context
// lives at a package subpath ("@toolpad/core/shared/context") that isn't in the package's
// exports map, so it type-checks under tsc's looser resolution but Rollup refuses to
// bundle it. useColorScheme() reads/writes the exact same CSS-vars state Toolpad's
// ThemeSwitcher does under the hood, since both sit on the same theme passed to AppProvider.
//
// Deliberately NOT theme.palette.mode here: with cssVariables enabled, the theme object
// stays static across a toggle (colors switch via a CSS attribute, not by regenerating the
// theme), so palette.mode never changes and the button gets stuck re-issuing the same
// setMode call every click. mode/systemMode from useColorScheme() are the actual reactive
// state — resolved the same way Toolpad's own CssVarsPaletteModeProvider does.
function TranslatedThemeSwitcher() {
    const { t } = useTranslation();
    const theme = useTheme();
    const { mode, systemMode, setMode } = useColorScheme();

    const resolvedMode = !mode || mode === 'system' ? systemMode : mode;
    const isDark = resolvedMode === 'dark';
    const label = t(isDark ? 'common.lightMode' : 'common.darkMode');

    return (
        <Tooltip title={label} arrow>
            <IconButton
                aria-label={label}
                onClick={() => setMode(isDark ? 'light' : 'dark')}
                sx={{ color: (theme.vars ?? theme).palette.primary.main }}
            >
                {isDark ? <LightMode /> : <DarkMode />}
            </IconButton>
        </Tooltip>
    );
}

function ToolbarActions() {
    const { t } = useTranslation();
    const { isArabic, switchLanguage, aiPanelOpen, setAiPanelOpen } = React.useContext(LayoutContext);
    const { user } = useAuth();
    usePWAUpdate();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [settingsAnchorEl, setSettingsAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleLanguageSwitch = () => {
        switchLanguage(isArabic ? LanguageCode.EN : LanguageCode.AR);
    };

    // Lets tapping a settings-row label act exactly like tapping its control (bigger,
    // more forgiving tap target on mobile) — forwards the click to the actual
    // button/switch/input rendered next to it instead of duplicating each control's
    // handler, so it stays correct even for controls with no exposed onClick (e.g.
    // Toolpad's ThemeSwitcher only exposes its toggle via the button it renders).
    const forwardClickToRowControl = (e: React.MouseEvent<HTMLElement>) => {
        const control = e.currentTarget.parentElement?.querySelector('button, input');
        (control as HTMLElement | null)?.click();
    };

    // Below `sm`, the underlying Toolpad toolbar wraps the language/theme
    // controls onto a second line when there isn't enough horizontal space —
    // collapse them into a single settings menu so the toolbar stays on one line.
    if (isMobile) {
        return (
            <>
                <IconButton
                    onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                    aria-label={t('common.displaySettings')}
                    size="small"
                >
                    <SettingsIcon fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={settingsAnchorEl}
                    open={!!settingsAnchorEl}
                    onClose={() => setSettingsAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {user?.isStudent && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1 }}>
                            <Typography variant="body2" onClick={forwardClickToRowControl} sx={{ cursor: 'pointer' }}>{t('notifications.title')}</Typography>
                            <NotificationBell size="small" />
                        </Box>
                    )}
                    {user?.isAdmin && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1 }}>
                            <Typography variant="body2" onClick={forwardClickToRowControl} sx={{ cursor: 'pointer' }}>{t('ai.chat.openTooltip')}</Typography>
                            <AIChatTriggerButton open={aiPanelOpen} onClick={() => setAiPanelOpen(true)} size="small" />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1 }}>
                        <Typography variant="body2" onClick={forwardClickToRowControl} sx={{ cursor: 'pointer' }}>{t('common.switchLanguage')}</Typography>
                        <LanguageSwitchComponent checked={isArabic} onChange={handleLanguageSwitch} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1 }}>
                        <Typography variant="body2" onClick={forwardClickToRowControl} sx={{ cursor: 'pointer' }}>{t('common.appearance')}</Typography>
                        <TranslatedThemeSwitcher />
                    </Box>
                </Menu>
            </>
        );
    }

    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
            }}>
                {user?.isStudent && <NotificationBell />}
                {user?.isAdmin && <AIChatTriggerButton open={aiPanelOpen} onClick={() => setAiPanelOpen(true)} />}
                <LanguageSwitchComponent checked={isArabic} onChange={handleLanguageSwitch} />
                <Box sx={{
                    '& .MuiIconButton-root': {
                        transition: 'transform 0.2s ease',
                        '&:hover': { transform: 'rotate(20deg)' },
                    },
                }}>
                    <TranslatedThemeSwitcher />
                </Box>
            </Box>
        </>
    );
}

// Replaces the popover's default content (AccountPreview + Divider + sign-out button) with the
// same pieces plus a "My Profile" row in between — Toolpad's `popoverContent` slot fully replaces
// the default content rather than wrapping it (see @toolpad/core/Account/Account.js), so this
// reproduces the default layout by hand instead of only being able to append to it.
function AccountPopoverContent() {
    const { t } = useTranslation();
    const { user, tenantLogoUrl } = useAuth();
    const navigate = useNavigate();

    const avatarSlotProps: AvatarProps = {
        src: tenantLogoUrl || '',
        children: <PersonRounded fontSize="small" sx={{ color: '#fff' }} />,
        sx: (theme: Theme) => ({
            ...(!tenantLogoUrl && { backgroundImage: getShellGradient(theme) }),
        }),
        ...(tenantLogoUrl && { slotProps: { img: { style: { objectFit: 'contain' as const, padding: 2 } } } }),
    };

    // Two separate profile pages exist (student self-service vs. staff/admin) since they edit
    // different underlying records (Student vs. User) — route to whichever applies.
    const handleProfileClick = () => {
        navigate(user?.isStudent ? '/my-profile' : '/profile');
    };

    // A platform admin impersonating a tenant has no real User row here — same reasoning
    // navigation.tsx already applies to the sidebar's own "Profile" item — so there's nothing for
    // either profile page to show; their own profile lives in epms.platform instead.
    const showProfileItem = !user?.isPlatformAdminImpersonation;

    return (
        <Stack direction="column">
            <AccountPopoverHeader>
                <AccountPreview variant="expanded" slotProps={{ avatar: avatarSlotProps }} />
            </AccountPopoverHeader>
            <Divider />
            {showProfileItem && (
                <>
                    <MenuList sx={{ px: 1 }}>
                        <MenuItem onClick={handleProfileClick}>
                            <ListItemIcon>
                                <PersonRounded fontSize="small" />
                            </ListItemIcon>
                            {t(user?.isStudent ? 'nav.myProfile' : 'nav.profile')}
                        </MenuItem>
                    </MenuList>
                    <Divider />
                </>
            )}
            <AccountPopoverFooter>
                <SignOutButton sx={{ borderRadius: '10px', fontWeight: 600, textTransform: 'none' }} variant="contained" />
            </AccountPopoverFooter>
        </Stack>
    );
}

function ToolbarAccount() {
    const { t } = useTranslation();
    const { tenantLogoUrl } = useAuth();
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <Account localeText={{
                accountSignInLabel: t('auth.signIn'),
                accountSignOutLabel: t('auth.signOut'),
            }}
                slots={{
                    popoverContent: AccountPopoverContent,
                }}
                slotProps={{
                    signInButton: {
                        startIcon: <Login />,
                        variant: 'contained',
                        title: t('auth.signIn'),
                        sx: {
                            width: 'fit-content',
                            borderRadius: '10px',
                            fontWeight: 600,
                            textTransform: 'none',
                            transition: 'all 0.25s ease',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: (theme: Theme) =>
                                    `0 4px 14px ${alpha(theme.palette.info.main, 0.4)}`,
                            },
                        },
                        color: 'info',
                    },
                    preview: {
                        slotProps: {
                            avatar: {
                                src: tenantLogoUrl || '',
                                children: <PersonRounded fontSize="small" sx={{ color: '#fff' }} />,
                                sx: (theme: Theme) => ({
                                    ...(!tenantLogoUrl && { backgroundImage: getShellGradient(theme) }),
                                }),
                                ...(tenantLogoUrl && { slotProps: { img: { style: { objectFit: 'contain', padding: 2 } } } }),
                            },
                        },
                    },
                }}
            />
        </Box>
    );
}

const LayoutContext = React.createContext<{
    setDirection: React.Dispatch<React.SetStateAction<'ltr' | 'rtl'>>;
    aiPanelOpen: boolean;
    setAiPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isArabic: boolean;
    switchLanguage: (langCode: string) => void;
}>({
    setDirection: () => { },
    aiPanelOpen: false,
    setAiPanelOpen: () => { },
    isArabic: true,
    switchLanguage: () => { },
});

export { LayoutContext };

export default function Layout({ setDirection }: LayoutProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, userRoles, user, tenantName, tenantLogoUrl, tenantPrimaryColor, tenantSecondaryColor, isLoading, logout } = useAuth();
    const [aiPanelOpen, setAiPanelOpen] = React.useState(false);
    const [isArabic, setIsArabic] = React.useState(() => {
        const saved = localStorage.getItem('language');
        return saved ? saved === LanguageCode.AR : true;
    });

    React.useEffect(() => {
        const savedLanguage = localStorage.getItem('language') || LanguageCode.AR;
        i18n.changeLanguage(savedLanguage);
        setDirection(savedLanguage === LanguageCode.AR ? 'rtl' : 'ltr');
        setIsArabic(savedLanguage === LanguageCode.AR);
    }, []);

    const switchLanguage = React.useCallback((langCode: string) => {
        i18n.changeLanguage(langCode);
        localStorage.setItem('language', langCode);
        setDirection(langCode === LanguageCode.AR ? 'rtl' : 'ltr');
        setIsArabic(langCode === LanguageCode.AR);
    }, [setDirection]);

    // App router
    const router: Router = React.useMemo(
        () => ({
            pathname: location.pathname,
            searchParams: new URLSearchParams(location.search),
            navigate: (path: string | URL) => navigate(String(path)),
        }),
        [location.pathname, location.search, navigate]
    );

    // Session based on auth context user
    const session: Session | null = user
        ? {
            user: {
                name: user.name,
                email: user.email,
                image: user.image,
            },
        }
        : null;

    // Authentication handlers
    const authentication = React.useMemo(
        () => ({
            signIn: async () => {
                navigate('/login');
            },
            signOut: async () => {
                logout();
                navigate('/login');
            },
        }),
        [logout, navigate]
    );

    // Branding with tenant logo or fallback icon — show tenant name if available, fallback to system name
    const branding = {
        title: tenantName || t('dashboard.systemName'),
        logo: tenantLogoUrl
            ? <BrandingLogo src={tenantLogoUrl} alt="logo" />
            : <School sx={{ fontSize: 32, color: 'primary.main' }} />,
    };

    // Dynamic navigation
    // Same OR logic as the nav item's own visibility check (getNavigation's hasAccess) and the
    // backend's [RequirePermission] (see RequirePermissionAttribute) — a plain Administrator can
    // see and use the whole Enrollment Requests feature without the specific claim ever being
    // assigned, so gating the badge on the claim alone left it permanently at 0 for them.
    const pendingEnrollmentRequestsCount = useEnrollmentRequestsPendingCount(
        isAuthenticated && (userRoles.includes(SystemRole.Administrator) || userRoles.includes(SystemClaim.AccessEnrollmentRequestsTab))
    );
    const navigation: Navigation = getNavigation(userRoles, isAuthenticated, user?.isPlatformAdminImpersonation, pendingEnrollmentRequestsCount);

    // Theme
    const theme = getTheme(i18n.language === LanguageCode.AR ? 'rtl' : 'ltr', {
        primaryColor: tenantPrimaryColor,
        secondaryColor: tenantSecondaryColor,
    });

    // Check if current route is login/register page or immersive view
    const isLoginPage = location.pathname === '/login';
    const isRegisterPage = location.pathname === '/register';
    const isCourseDetailRoute = /^\/my-courses\/[^/]+$/.test(location.pathname);
    const isTakeExamRoute = /^\/my-exams\/[^/]+\/take$/.test(location.pathname);
    const isImmersiveRoute = isCourseDetailRoute;

    // Render login page outside the DashboardLayout
    if (isLoginPage) {
        return (
            <LayoutContext.Provider value={{ setDirection, aiPanelOpen, setAiPanelOpen, isArabic, switchLanguage }}>
                <AppProvider
                    session={session}
                    authentication={authentication}
                    navigation={navigation}
                    router={router}
                    branding={branding}
                    theme={theme}
                >
                    <React.Suspense fallback={<PageFallback />}>
                        <LoginPage />
                    </React.Suspense>
                </AppProvider>
            </LayoutContext.Provider>
        );
    }

    // Render register page outside the DashboardLayout — public, unauthenticated
    // (reached via the "Enroll Now" link on the tenant's epms.landing site)
    if (isRegisterPage) {
        return (
            <LayoutContext.Provider value={{ setDirection, aiPanelOpen, setAiPanelOpen, isArabic, switchLanguage }}>
                <AppProvider
                    session={session}
                    authentication={authentication}
                    navigation={navigation}
                    router={router}
                    branding={branding}
                    theme={theme}
                >
                    <React.Suspense fallback={<PageFallback />}>
                        <RegisterPage />
                    </React.Suspense>
                </AppProvider>
            </LayoutContext.Provider>
        );
    }

    // Render take-exam page outside the DashboardLayout (fully immersive, no app bar)
    if (isTakeExamRoute) {
        return (
            <LayoutContext.Provider value={{ setDirection, aiPanelOpen, setAiPanelOpen, isArabic, switchLanguage }}>
                <AppProvider
                    session={session}
                    authentication={authentication}
                    navigation={navigation}
                    router={router}
                    branding={branding}
                    theme={theme}
                >
                    <React.Suspense fallback={<PageFallback />}>
                        <Routes>
                            <Route element={<ProtectedRoute requiredRoles={[SystemRole.Student]} />}>
                                <Route path="my-exams/:examSignature/take" element={<TakeExamPage />} />
                            </Route>
                        </Routes>
                    </React.Suspense>
                </AppProvider>
            </LayoutContext.Provider>
        );
    }

    // While auth is loading, don't render the DashboardLayout at all
    if (isLoading) {
        return null;
    }

    // Redirect unauthenticated users to login — prevents showing DashboardLayout
    // (with unauthorized/not-found pages) to users who aren't logged in
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <LayoutContext.Provider value={{ setDirection, aiPanelOpen, setAiPanelOpen, isArabic, switchLanguage }}>
            <AppProvider
                session={session}
                authentication={authentication}
                navigation={navigation}
                router={router}
                branding={branding}
                theme={theme}
            >
                <DashboardLayout
                    hideNavigation={isImmersiveRoute}
                    sidebarExpandedWidth={240}
                    slots={{
                        toolbarActions: ToolbarActions,
                        toolbarAccount: ToolbarAccount,
                        sidebarFooter: AppVersionFooter,
                    }}
                    sx={getDashboardLayoutSx(theme)}
                >
                    {isImmersiveRoute ? (
                        <Box
                            key={location.pathname}
                            sx={{
                                height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
                                overflow: 'hidden',
                            }}
                        >
                            <React.Suspense fallback={<PageFallback />}>
                                <Routes>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Student]} />}>
                                        <Route path="my-courses/:signature" element={<MyCourseDetailPage />} />
                                    </Route>
                                </Routes>
                            </React.Suspense>
                        </Box>
                    ) : (
                        <PageContent key={location.pathname} maxWidth="xl">
                            <React.Suspense fallback={<PageFallback />}>
                                <Routes>
                                    <Route element={<ProtectedRoute />}>
                                        <Route path="" element={<DashboardPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Student]} />}>
                                        <Route path="my-courses" element={<MyCoursesPage />} />
                                        <Route path="my-exams" element={<MyExamsPage />} />
                                        {/* Courses/exams catalog is now a tab on My Courses/My Exams rather than its own
                                            page — these old URLs still work, landing straight on that tab. */}
                                        <Route path="courses-catalog" element={<Navigate to="/my-courses?tab=browse" replace />} />
                                        <Route path="exams-catalog" element={<Navigate to="/my-exams?tab=browse" replace />} />
                                        <Route path="my-profile" element={<StudentProfilePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessSubjectsTab]} />}>
                                        <Route path="subjects" element={<SubjectTreePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessCoursesTab]} />}>
                                        <Route path="courses" element={<CoursePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessCoursesTab]} />}>
                                        <Route path="course-items/:signature" element={<CourseItemPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessQuestionsBanksTab]} />}>
                                        <Route path="questions-banks" element={<QuestionsBankPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessQuestionsTab]} />}>
                                        <Route path="questions" element={<QuestionPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessExamsTab]} />}>
                                        <Route path="exams" element={<ExamPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessPricingPlansTab]} />}>
                                        <Route path="pricing-plans" element={<PricingPlanPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessLicensesTab]} />}>
                                        <Route path="licenses" element={<LicensePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessEnrollmentRequestsTab]} />}>
                                        <Route path="enrollment-requests" element={<EnrollmentRequestsPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessRolesTab]} />}>
                                        <Route path="role-table" element={<RolePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessUsersTab]} />}>
                                        <Route path="user-table" element={<UserPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessStudentsTab]} />}>
                                        <Route path="student-table" element={<StudentManagementPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessStudentResultsTab]} />}>
                                        <Route path="student-results" element={<StudentResultsPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessLiveRoomsTab]} />}>
                                        <Route path="live-rooms" element={<LiveRoomsPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessAttendanceTab]} />}>
                                        <Route path="attendance" element={<AttendancePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessFinancialReportsTab]} />}>
                                        <Route path="financial-reports" element={<FinancialReportsPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Student]} />}>
                                        <Route path="my-live-rooms" element={<MyLiveRoomsPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemRole.Student, SystemClaim.AccessLiveRoomsTab]} />}>
                                        <Route path="live-rooms/:roomName/room" element={<LiveRoomPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessUsersTab, SystemClaim.AccessRolesTab, SystemClaim.AccessStudentsTab, SystemClaim.AccessStudentResultsTab]} blockPlatformAdminImpersonation />}>
                                        <Route path="profile" element={<UserProfilePage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemClaim.AccessTenantSettingsTab]} />}>
                                        <Route path="tenant-branding" element={<TenantBrandingPage />} />
                                        <Route path="tenant-integrations" element={<TenantIntegrationsPage />} />
                                        <Route path="tenant-payment-channels" element={<TenantPaymentChannelsPage />} />
                                    </Route>
                                    <Route element={<ProtectedRoute requiredRoles={[SystemRole.Administrator, SystemRole.Student]} />}>
                                        <Route path="more" element={<MorePage />} />
                                    </Route>
                                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </React.Suspense>
                        </PageContent>
                    )}
                </DashboardLayout>
                {!isImmersiveRoute && <BottomNavBar />}
                {user?.isAdmin && (
                    <AIChatPanel open={aiPanelOpen} onClose={() => setAiPanelOpen(false)} />
                )}
            </AppProvider>
        </LayoutContext.Provider>
    );
}
